import { useDispatch } from 'react-redux';
import { useEffect, useState, useMemo } from 'react';
import _ from 'lodash';
import { RootState, useSelector } from 'store';
import { setActiveProject, ActiveProjectKey } from 'store/additional/activeProjectsSlice';
import { SyntheticEvent } from 'react-draft-wysiwyg';
import { useGetApiUserPreferencesParsedQuery } from 'store/userOrg/enhancedUserOrgApi';
import { Box } from '@mui/system';
import { TabContext, TabPanel } from '@mui/lab';
import { LinearProgress, Tab, Tabs } from '@mui/material';
import MainCard from 'components/ui/Card/MainCard';
import MetricsExplorer from './MetricsExplorer';
import ResourceStatistics from './ResourceStatistics';
import useCheckPermission from 'hooks/useCheckPermission';
import { permissions } from 'constants/permissions';
import useGAHandleClick from 'hooks/useGAHandleClick';
import Welcome from 'components/features/welcome/Welcome';
import ProjectTabs from 'components/features/ProjectTabs';
import { isProjectExpired } from 'utils/project';
import { useGetProjectData } from 'hooks/useGetProjectData';

const DDI = () => {
    const googleAnalytics = useGAHandleClick();
    const { data: preferences, isLoading: preferencesIsLoading } = useGetApiUserPreferencesParsedQuery();
    const selectedGlobalFilterValues = preferences ? preferences?.globalFilterValues : [];

    const { data: projects, isLoading: projectsIsLoading } = useGetProjectData({});
    const checkPerms = useCheckPermission();
    const isPlatformAdmin = checkPerms(permissions.platformadmin, '');

    const projectsWithExpired = useMemo(() => {
        const filtered =
            projects?.filter(project => selectedGlobalFilterValues?.includes(project.ShortName as string)) || [];
        const sorted = _.sortBy(filtered, [p => isProjectExpired(p), p => p?.ShortName]);
        return isPlatformAdmin ? ['*', ...sorted] : sorted;
    }, [projects, selectedGlobalFilterValues, isPlatformAdmin]);

    const availableProjects = useMemo(
        () => projectsWithExpired.map(p => (typeof p === 'string' ? p : p?.ShortName)),
        [projectsWithExpired]
    );

    const dispatch = useDispatch();
    const [resetRowSelection, setResetRowSelection] = useState(false);

    const selectedProject = useSelector((state: RootState) => state.activeProjects[ActiveProjectKey.DdiDashboard]);

    useEffect(() => {
        if (projects && !availableProjects.includes(selectedProject)) {
            dispatch(setActiveProject({ key: ActiveProjectKey.DdiDashboard, project: availableProjects[0] || '1' }));
        }
    }, [projectsWithExpired, selectedProject, dispatch, projects, availableProjects]);

    useEffect(() => {
        resetRowSelection && setResetRowSelection(false);
    }, [resetRowSelection]);

    const handleChange = (_: SyntheticEvent, newValue: string) => {
        if (newValue !== selectedProject) {
            dispatch(setActiveProject({ key: ActiveProjectKey.DdiDashboard, project: newValue }));
            googleAnalytics({ label: 'SelectDDIProjectTab', action: 'Select', category: 'Selecting' });
        }
    };

    const [subTab, setSubTab] = useState('metrics');

    const handleSubTabChange = (event: React.SyntheticEvent, newValue: string) => {
        setSubTab(newValue);
    };

    return (
        <MainCard>
            {projectsIsLoading || preferencesIsLoading ? (
                <LinearProgress />
            ) : projects?.length ? (
                <Box sx={{ width: '100%', typography: 'body1' }}>
                    {availableProjects?.includes(selectedProject) && (
                        <TabContext value={selectedProject ?? ''}>
                            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                <ProjectTabs
                                    value={selectedProject ?? ''}
                                    onChange={handleChange}
                                    projects={projectsWithExpired}
                                />
                            </Box>

                            <TabPanel value={selectedProject ?? ''} key={selectedProject}>
                                {selectedProject !== '*' ? (
                                    <>
                                        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                                            <Tabs
                                                value={subTab}
                                                onChange={handleSubTabChange}
                                                aria-label='ddi sub tabs'>
                                                <Tab label='Metrics' value='metrics' />
                                                <Tab label='Resource Statistics' value='resources' />
                                            </Tabs>
                                        </Box>
                                        {subTab === 'metrics' && <MetricsExplorer project={selectedProject} />}
                                        {subTab === 'resources' && <ResourceStatistics project={selectedProject} />}
                                    </>
                                ) : (
                                    <MetricsExplorer project={selectedProject} />
                                )}
                            </TabPanel>
                        </TabContext>
                    )}
                </Box>
            ) : (
                <Welcome />
            )}
        </MainCard>
    );
};

export default DDI;
